import React, { useEffect, useState } from "react";
import { Button, Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { UserService } from "../../../core/services";
import { connect } from "react-redux";
import { FormValidation } from "../../../core/utility";
import { LocalService } from '../../../core/services';
import * as gtUtil from "../../../core/utility";


const userService = new UserService();
const localService = new LocalService();

interface Props {
    UserData: any;
}

const PinSecurity: React.FC<Props> = (props: Props) => {
    const [currentPin, setCurrentPin] = useState("");
    const [newPin, setNewPin] = useState("");
    const [confirmPin, setConfirmPin] = useState("");
    // Credentials stay masked by default; each field gets its own eye toggle.
    const [showCurrentPin, setShowCurrentPin] = useState(false);
    const [showNewPin, setShowNewPin] = useState(false);
    const [showConfirmPin, setShowConfirmPin] = useState(false);

    const [errors, setErrors] = useState<{
        currentPin?: string;
        newPin?: string;
        confirmPin?: string;
    }>({});

    const [timerState, setTimerState] = useState({
        isDisabledPIN: false,
        pinTimeLeft: 0
    });

    const sanitizeNumericInput = (val: string): string => {
        return val.replace(/[^0-9]/g, "");
    };


    const handleCurrentPinChange = (val: string) => {
        const numericVal = sanitizeNumericInput(val);
        setCurrentPin(numericVal);
        if (errors.currentPin) {
            setErrors((prev) => ({ ...prev, currentPin: "" }));
        }
    };

    const handleNewPinChange = (val: string) => {
        const numericVal = sanitizeNumericInput(val);
        setNewPin(numericVal);
        if (errors.newPin) {
            setErrors((prev) => ({ ...prev, newPin: "" }));
        }
    };

    const handleConfirmPinChange = (val: string) => {
        const numericVal = sanitizeNumericInput(val);
        setConfirmPin(numericVal);
        if (errors.confirmPin) {
            setErrors((prev) => ({ ...prev, confirmPin: "" }));
        }
    };


    const handleSave = async () => {
        const isEmpty = (val: string) => !val.trim();
        const newErrors: { [key: string]: string } = {};
        const isNumeric = (val: string) => /^\d+$/.test(val);


        if (isEmpty(currentPin)) {
            newErrors.currentPin = "Current PIN is required";
        }
        if (isEmpty(newPin)) {
            newErrors.newPin = "New PIN is required";
        }
        if (isEmpty(confirmPin)) {
            newErrors.confirmPin = "Please confirm your new PIN";
        }

        // Validate PIN format
        if (newPin && !isNumeric(newPin)) {
            newErrors.newPin = "PIN must be numeric only";
        } else if (newPin && newPin.length !== 4) {
            newErrors.newPin = "PIN must be exactly 4 digits";
        }

        if (confirmPin && !isNumeric(confirmPin)) {
            newErrors.confirmPin = "PIN must be numeric only";
        }

        // Validate that new and confirm PINs match
        if (newPin && confirmPin && newPin !== confirmPin) {
            newErrors.confirmPin = "PINs do not match";
        }


        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({}); // Clear errors if all validations pass

        try {
            const response = await userService.updatePin({
                data: {
                    old_pin: currentPin,
                    new_pin: newPin,
                    confirm_pin: confirmPin,
                    user_id: props.UserData.user_id,
                },
            });

            if (response.status === gtUtil.Constants.SUCCESS) {
                localService.toastify("PIN Updated", "success", 1000);
                setCurrentPin("");
                setNewPin("");
                setConfirmPin("");
                setErrors({});
            } else {
                localService.toastify(response.data.info, "error", 1000);
                if (response.data.info === 'Current PIN is incorrect') {
                    setErrors({ currentPin: response.data.info });
                }
            }
        } catch (error) {
            localService.toastify("PIN update failed", "error", 1000);
        }
    };

    const forgetPasswordPin = (login: string, forgot: string) => {
        if (!login.trim()) {
            localService.toastify("Valid Email or Mobile Number is required", "error", 1000);
            return;
        }

        userService.forgetPasswordPin({ login, forgot }).then((res) => {
            if (res.status === gtUtil.Constants.SUCCESS) {
                localService.toastify(res.info, "success", 1000);

                const countdownTime = 180; // 3 minutes

                setTimerState((prevState) => ({
                    ...prevState,
                    isDisabledPIN: forgot === "forgot pin" ? true : prevState.isDisabledPIN,
                    pinTimeLeft: forgot === "forgot pin" ? countdownTime : prevState.pinTimeLeft
                }));

                setTimeout(() => {
                    setTimerState((prevState) => ({
                        ...prevState,
                        isDisabledPIN: forgot === "forgot pin" ? false : prevState.isDisabledPIN
                    }));
                }, countdownTime * 1000);
            } else {
                setTimerState({
                    isDisabledPIN: false,
                    pinTimeLeft: 0,
                });
                localService.toastify(res.info, "error", 1000);
            }
        });
    };

    useEffect(() => {
        if (timerState.pinTimeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimerState((prevState) => ({
                ...prevState,
                pinTimeLeft: prevState.pinTimeLeft > 0 ? prevState.pinTimeLeft - 1 : 0
            }));
        }, 1000);

        return () => clearInterval(timer);
    }, [timerState.pinTimeLeft]);


    return (
        <>
            <span className="fs-16 d-none d-md-block" style={{ marginBottom: "10px" }}>
                PIN
            </span>
            <Grid container spacing={2}>
                <Grid item xs={12} className="d-flex flex-column align-items-end">
                    <TextField
                        label="Current PIN"
                        value={currentPin}
                        onChange={(e) => handleCurrentPinChange(e.target.value)}
                        fullWidth
                        inputProps={{
                            maxLength: 4,
                        }}
                        type={showCurrentPin ? "text" : "password"}
                        size="small"
                        className="input-common"
                        error={!!errors.currentPin}
                        helperText={errors.currentPin}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={`${showCurrentPin ? "Hide" : "Show"} current PIN`}
                                        onClick={() => setShowCurrentPin(!showCurrentPin)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end"
                                        size="small"
                                    >
                                        {showCurrentPin ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="d-flex pb-2">
                        <span
                            style={{
                                fontSize: '12px',
                                color: timerState.isDisabledPIN ? '#aaa' : '#000',
                                cursor: timerState.isDisabledPIN ? 'not-allowed' : 'pointer',
                                pointerEvents: timerState.isDisabledPIN ? 'none' : 'auto'
                            }}
                            onClick={() => {
                                if (!timerState.isDisabledPIN) {
                                    forgetPasswordPin(props.UserData.login_email, "forgot pin");
                                }
                            }}
                        >
                            Forgot PIN?
                        </span>
                        {timerState.isDisabledPIN && timerState.pinTimeLeft > 0 && (
                            <span className="error" style={{ marginLeft: '8px', fontSize: '0.8rem' }}>
                                {Math.floor(timerState.pinTimeLeft / 60)}:
                                {(timerState.pinTimeLeft % 60).toString().padStart(2, "0")}
                            </span>
                        )}
                    </div>
                </Grid>
                <Grid item xs={12} className="pt-0">
                    <TextField
                        label="New PIN"
                        value={newPin}
                        onChange={(e) => handleNewPinChange(e.target.value)}
                        fullWidth
                        inputProps={{
                            maxLength: 4
                        }}
                        type={showNewPin ? "text" : "password"}
                        size="small"
                        className="input-common"
                        error={!!errors.newPin}
                        helperText={errors.newPin}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={`${showNewPin ? "Hide" : "Show"} new PIN`}
                                        onClick={() => setShowNewPin(!showNewPin)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end"
                                        size="small"
                                    >
                                        {showNewPin ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Confirm PIN"
                        value={confirmPin}
                        onChange={(e) => handleConfirmPinChange(e.target.value)}
                        fullWidth
                        inputProps={{
                            maxLength: 4
                        }}
                        type={showConfirmPin ? "text" : "password"}
                        size="small"
                        className="input-common"
                        error={!!errors.confirmPin}
                        helperText={errors.confirmPin}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={`${showConfirmPin ? "Hide" : "Show"} confirm PIN`}
                                        onClick={() => setShowConfirmPin(!showConfirmPin)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        edge="end"
                                        size="small"
                                    >
                                        {showConfirmPin ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        className="btn btn-outline-primary upload_button"
                        onClick={handleSave}
                    >
                        Update PIN
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};
const mapStateToProps = (state: any) => {
    return {
        UserData: state.UserReducer.UserData,
    };
};
const connectToRedux = connect(mapStateToProps, null)(PinSecurity);
export { connectToRedux as PinSecurity };